import React, { useState, useEffect } from 'react';
import {filter, map, startCase} from "lodash";
import {durationSortComparator, formatDate, formatFullDate, getErrorMessage, getPeriodEndLabel, isCurrentYearSelected, overlapsContract} from "utility/helpers/utils";
import { getTimeOff } from "utility/apis/ConfigService";
import { TABLES_NAMES } from "utility/constants/constants";
import StatusCard from "components/StatusCard/StatusCard";
import { TIME_OFF_REQUEST_MODAL, CONFIRMATION_MODAL, TIME_OFF_CONVERT_REQUEST_MODAL, TIME_OFF_CANCEL_REQUEST_MODAL } from "utility/modals/constants";
import {
    cancelTimeOffRequest,
    convertTimeOffRequest,
    deleteTimeOffRequest,
    getBalanceDeduction,
    updateTimeOffRequest
} from "utility/apis/EmployeeService";
import {CancelIcon, DeleteIcon, EditIcon, RedoIcon} from "components/Mui/Icons";
import {
    Button,
    DataGridTable,
    DialogActions,
    GridActionsCellItem,
    Dialog,
    DateTimePicker,
    Select,
    Tooltip,
    Card, Divider
} from "components/Mui";
import Loading from "components/Loading/Loading";
import {DATE_FORMAT, DAY_MONTH_FORMAT, FULL_DATE_ISO_FORMAT, MONTH_FORMAT, TIME_FORMAT} from "utility/helpers/metaData";
import {timeOffOptions} from "utility/constants/data";
import {Controller, useForm} from "react-hook-form";
import WithModals from "hoc/withModals/withModals";
import moment from "moment";
import "./TimeOffRequests.scss";
import {getSystemConfiguration} from "utility/apis/SettingService";
import {toast} from "react-toastify";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import {Typography} from "@mui/material";

const TimeOffRequests = (props) => {
    const { employeeId, date, message, colorTheme, modals, toggleTimeOffEditModal, toggleConfirmationModal, toggleTimeOffCancelModal, toggleTimeOffConvertModal, tenantId, contractId, contract } = props;
    const [ inProgress, setInProgress ] = useState(false);
    const [ statusCard, setStatusCard ] = useState({status: {}, oneCardLabel: ''});
    const [dataTable, setDataTable] = useState([]);
    const [balanceDataTable, setBalanceDataTable] = useState([]);
    const [selectedTimeOffRequest, setSelectedTimeOffRequest] = useState({});
    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [isInProgress, setIsInProgress] = useState(false);
    const [table1Height, setTable1Height] = useState(400);
    const [table2Height, setTable2Height] = useState(175);
    const {  control, handleSubmit } = useForm({defaultValues: {}});
    const fetchData = async () => {
        setInProgress(true);
        try {
            const response = await getTimeOff(employeeId, moment.isMoment(date)? date.format("YYYY") : moment.isMoment(date.value)? moment(date.value).format("YYYY") : date?.value, 'personal', moment.isMoment(date)? date.format(DATE_FORMAT): moment.isMoment(date.value)? moment(date.value).format(DATE_FORMAT) : `${date?.value}-12-31`, contractId);
            const data = formatDataTable(response?.data?.mergedVacations);
            const systemConfig = await getSystemConfiguration(tenantId);
            let weeksSummaryEnabled;
            systemConfig?.data.forEach((row) => {
                if(row.name === "Weeks-Summary" && row.enabled) weeksSummaryEnabled = true;
            });
            const isYearEnd = (() => {
                if (moment.isMoment(date) && date.isSame(moment(date).endOf('year'), 'day')) {
                    return true;
                }
                if (date?.toDate) {
                    const parsedDay = moment(date.toDate);
                    if (parsedDay && parsedDay.isSame(moment(parsedDay).endOf('year'), 'day')) {
                        return true;
                    }
                }
                return false;
            })();
            if(weeksSummaryEnabled) {
                setStatusCard({
                    status: {
                        'Total Balance': response?.data?.totalVacationDaysToDateInWeeks,
                        'Used Time Off & Deductions': response?.data?.totalDeductionsInWeeks,
                        'Remaining Balance': response?.data?.totalVacationDaysToDateInWeeks - response?.data?.totalDeductionsInWeeks
                    },
                    valueLabel: 'weeks'
                });
            } else {
                setStatusCard({
                    status: {
                        'Total Balance': response?.data?.totalVacationDaysToDate,
                        'Used Time Off & Deductions': response?.data?.totalDeductions,
                        'Remaining Balance': response?.data?.totalVacationDaysToDate - response?.data?.totalDeductions
                    },
                    valueLabel: 'days'
                });
            }
            setDataTable(data);
            const balanceDeductionResponse = await getBalanceDeduction(employeeId, moment.isMoment(date)? date.format("YYYY") : moment.isMoment(date.value)? moment(date.value).format("YYYY") : date?.value,
                date?.label?.includes('-') ? date?.label : '');
            // Only deductions dated inside the selected contract period, matching the Balance
            // Deduction tab. The endpoint returns the whole year, so without this a deduction from
            // before the contract started is still listed here.
            const balanceDeductions = filter(balanceDeductionResponse?.data,
                (row) => overlapsContract(row?.date, row?.date, contract));
            setBalanceDataTable(balanceDeductions);
            setTable1Height(balanceDeductions?.length > 0 ? 400 : 600)
        } catch (e) {
            toast.error(getErrorMessage(e));
        }
        setInProgress(false);
    }

    useEffect(() => {
        if (date) {
            fetchData();
        }
    }, [date, contractId]);

    const refreshData = () => {
        fetchData() ;
    }

    const formatDataTable = (data) => {
        return map(data, ({ fromDate, originalFromDate, toDate, originalToDate, hours, value, minutes, mergedVacation, vacationLengthLabel }, index) => {
            const newMergedVacations = {
                state: [],
                approver: [],
                comments: []
            };
            map(mergedVacation, ({ vacationState, attendanceState, teamManagerApprovals, comments }) =>  {
                newMergedVacations.state.push(attendanceState || vacationState);
                newMergedVacations.comments.push(comments);
                if (teamManagerApprovals) {
                    teamManagerApprovals.forEach((teamManagerApproval) => {
                        newMergedVacations.approver.unshift(
                            ...map(teamManagerApproval?.managerApprovalSet, ({
                                state,
                                managerName,
                                comments
                            }) => ({
                                managerName,
                                state,
                                comments
                            }))
                        );
                    });
                }
            });
            return {
                id: index,
                idRequest: mergedVacation[0].id,
                type: mergedVacation[0].type,
                originalFromDate: formatFullDate(new Date(originalFromDate)),
                fromDate: formatFullDate(fromDate),
                originalToDate: formatFullDate(new Date(originalToDate)),
                toDate: formatFullDate(toDate),
                details: vacationLengthLabel,
                state: newMergedVacations.state,
                comments: newMergedVacations.comments,
                approver: newMergedVacations.approver,
                mergedVacation
            }
        });
    }

    const columnDefs = [
        {
            headerName: 'Details',
            field: 'details',
            flex: 1.7,
            sortable: true,
            valueGetter: (params) => params?.value || '',
            sortComparator: durationSortComparator
        },
        {
            headerName: 'From Date',
            field: 'fromDate',
            flex: 2,
            renderCell: (params) => {
                const showWarning = formatFullDate(params.row?.originalFromDate) !== params.row?.fromDate;
                return (
                    <div className="ellipsis" style={{display: 'flex', alignItems: 'center'}}>
                        <span>{params.row.fromDate}</span>
                        {showWarning && (
                            <Tooltip title={
                                <span>
      This is a splitted vacation, the original From Date is
      <br/>
                                    {formatFullDate(params.row?.originalFromDate)}
    </span>
                            }>
                        <span style={{color: 'orange', marginLeft: '0.4rem', cursor: 'pointer'}}>
                            ⚠️
                        </span>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            headerName: 'To Date',
            field: 'toDate',
            flex: 2,
            renderCell: (params) => {
                const showWarning = formatFullDate(params.row?.originalToDate) !== params.row?.toDate;
                return (
                    <div className="ellipsis" style={{display: 'flex', alignItems: 'center'}}>
                        <span>{params.row.toDate}</span>
                        {showWarning && (
                            <Tooltip title={
                                <span>
      This is a splitted vacation, the original To Date is
      <br/>
                                    {formatFullDate(params.row?.originalToDate)}
    </span>
                            }>
                        <span style={{color: 'orange', marginLeft: '0.4rem', cursor: 'pointer'}}>
                            ⚠️
                        </span>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },

        {
            headerName: 'Approver',
            field: 'approver',
            renderCell: (params) => (
                <div className="ellipsis">
                    {params.value?.map(({ state, managerName }) => (
                        <Tooltip title={`${startCase(managerName)}: ${startCase(state)}`}>
                            <div className="ellipsis approvers-list-info">
                                <span>{startCase(managerName)}: </span>
                                <span className={state.indexOf('Rejected') > -1 ? "text-danger ellipsis" : "text-info ellipsis"}>{startCase(state)}</span>
                            </div>
                        </Tooltip>
                    ))}
                    {/*<span className={`ms-2 ${params.row.approver >= 0 ? "text-success" : "text-danger"}`}>*/}
                    {/*    {params.row.approver}*/}
                    {/*</span>*/}
                </div>
            ),
            flex: 2,
            sortable: false,
        },
        {
            headerName: 'Submitted By',
            field: 'byWhome',
            flex: 1.4,
            valueGetter: (params) => params.row?.mergedVacation?.[0]?.byWhome || '',
            renderCell: (params) => (
                <div className="ellipsis">
                    <Tooltip title={params?.row?.mergedVacation[0]?.byWhome}>
                        <div className="label-font ellipsis">{params?.row?.mergedVacation[0]?.byWhome}</div>
                    </Tooltip>
                </div>

            ),
            sortable: true,
        },
        {
            headerName: 'Final State',
            field: 'state',
            flex: 1,
            valueGetter: (params) => params.row.state?.[0] || '',
            renderCell: (params) => (
                <div className="ellipsis">
                    {params.row.state?.map((text) => (
                        <Tooltip  title={startCase(text)}>
                            <div className={`ellipsis ${text.indexOf('Rejected') > -1 ? "text-danger" : "text-info"}`}>{startCase(text)}</div>
                        </Tooltip>
                    ))}
                </div>
            ),
        },
        {
            headerName: 'Comments',
            field: 'comments',
            flex: 1.5,
            valueGetter: (params) => {
                return (params.row?.comments || []).join(' ').toLowerCase();
            },
            renderCell: (params) => (
                <div className="ellipsis">
                    {(params.row?.comments || []).map((text, index) => (
                        <>
                            {text &&
                                <Tooltip title={startCase(text)} key={index}>
                                    <div className="ellipsis">{startCase(text)}</div>
                                </Tooltip>
                            }
                        </>
                    ))}
                </div>
            ),
        },
        {
            headerName: 'Actions',
            field: 'actions',
            type: 'actions',
            width: 80,
            renderHeader: () => (
                <div className="ellipsis">
                    <Tooltip title='Actions'>
                    Actions
                    </Tooltip>
                </div>
            ),
            getActions: (params) => {
                const actions = [];
                actions.push(
                    <GridActionsCellItem showInMenu icon={<EditIcon />} onClick={(event) => handleEditClick(event, params)} label="Edit" />,
                    <GridActionsCellItem showInMenu icon={<RedoIcon />} onClick={(event) => handleConvertClick(event, params)} label="Convert vacation" />,
                    <GridActionsCellItem sx={!isCurrentYearSelected(date)? {'color': '#cdcdcd', 'cursor': 'default'} : null} showInMenu icon={<CancelIcon sx={!isCurrentYearSelected(date)? {'color': '#cdcdcd', 'cursor': 'default'} : null}/>}
                         onClick={(event) => {
                             if (!isCurrentYearSelected(date)) {
                                 event.stopPropagation();
                                 return;
                             }
                             handleCancelClick(event, params);
                         }}
                    label="Cancel request" />,
                    <GridActionsCellItem sx={!isCurrentYearSelected(date)? {'color': '#cdcdcd !important', 'cursor': 'default'} : null} showInMenu icon={<DeleteIcon sx={!isCurrentYearSelected(date)? {'color': '#cdcdcd !important', 'cursor': 'default'} : null}/>}
                         onClick={(event) => {
                             if (!isCurrentYearSelected(date)) {
                                 event.stopPropagation();
                                 return;
                             }
                             handleDeleteClick(event, params);
                         }}
                    label="Delete" className="text-danger" />
                );
                return actions;
            },
        }
    ];
    const balanceColumnDefs = [
        {
            headerName: 'Month',
            field: 'date',
            renderCell: (params) => (
                <Tooltip title={formatDate(params.value)}>
                    <div className="ellipsis">
                        {formatDate(params.value)}
                    </div>
                </Tooltip>
            ),
            flex: 1
        },
        {
            headerName: 'Number of hours',
            field: 'value',
            renderCell: (params) => (
                <Tooltip title={`${params.value} ${params.value > 1 ? 'hours' : 'hour'}`}>
                    <div className="ellipsis">
                        {parseFloat(params.value).toFixed(params.value % 1 === 0 ? 0 : 2)} {params.value > 1 ? 'hours' : 'hour'}
                    </div>
                </Tooltip>
            ),
            flex: 1.5
        },
        {
            headerName: 'Comment',
            field: 'comment',
            flex: 2
        },
        {
            headerName: 'Created',
            field: 'created',
            renderCell: (params) => (
                <Tooltip title={formatDate(params.value)}>
                    <div className="ellipsis">
                        {formatDate(params.value)}
                    </div>
                </Tooltip>
            ),
            flex: 1
        },
        {
            headerName: 'Submitted By',
            field: 'byWhomName',
            width: 110,
            sortable: true,
        },
    ];


    const handleEditClick = (event, params) => {
        setSelectedTimeOffRequest(params.row);
        setFromDate(moment(params.row.fromDate));
        setToDate(moment(params.row.toDate));
        toggleTimeOffEditModal();
    }

    const handleConvertClick = (event, params) => {
        setSelectedTimeOffRequest(params.row);
        toggleTimeOffConvertModal();
    }

    const handleCancelClick = (event, params) => {
        setSelectedTimeOffRequest(params.row);
        toggleTimeOffCancelModal();
    }
    const handleDeleteClick = (event, params) => {
        setSelectedTimeOffRequest(params.row);
        toggleConfirmationModal();
    }
    const handleTimeOffRequestUpdate = async (event, data) => {
        setIsInProgress(true);
        event.preventDefault();
        try {
            const {  toDate, fromDate } = data;
            const formattedFormDate = fromDate.format(FULL_DATE_ISO_FORMAT) + "Z";
            const formattedToDate = toDate.format(FULL_DATE_ISO_FORMAT) + "Z";

            const payload = {
                from: formattedFormDate,
                to: formattedToDate,
                employee_id: employeeId,
                type: selectedTimeOffRequest.type
            };
            await updateTimeOffRequest(selectedTimeOffRequest.idRequest, payload);
            toast.success("Updated Successfully");
            refreshData();
            toggleTimeOffEditModal();
        } catch (e) {
            toast.error(getErrorMessage(e));
        }finally {
            setTimeout(() => {
                setIsInProgress(false);
            }, 2000);
        }
    }
    const handleTimeOffRequestConvert = async (data) => {
        setIsInProgress(true);
        try {
            await convertTimeOffRequest(selectedTimeOffRequest.idRequest, data.timeOffOption);
            toast.success("Converted Successfully");
            refreshData();
            toggleTimeOffConvertModal();
        } catch (e) {
            toast.error(getErrorMessage(e));
        }finally {
            setTimeout(() => {
                setIsInProgress(false);
            }, 2000);
        }
    }
    const handleTimeOffRequestCancel = async () => {
        setIsInProgress(true);
        try {
            await cancelTimeOffRequest(selectedTimeOffRequest.idRequest);
            toast.success("Canceled Successfully");
            refreshData();
            toggleTimeOffCancelModal();
        } catch (e) {
            toast.error(getErrorMessage(e));
        }finally {
            setTimeout(() => {
                setIsInProgress(false);
            }, 2000);
        }
    }
    const handleTimeOffRequestDelete = async () => {
        setIsInProgress(true);
        try {
            await deleteTimeOffRequest(selectedTimeOffRequest.idRequest);
            toast.success("Deleted Successfully");
            refreshData();
            toggleConfirmationModal();
        } catch (e) {
            toast.error(getErrorMessage(e));
        }finally {
            setTimeout(() => {
                setIsInProgress(false);
            }, 2000);
        }
    }


    return (
        <div id="time-off" className="c-time-off">
            {inProgress ?
                <Loading></Loading>
                :
                <div className="container-fluid">
                    {!!statusCard &&
                        <div className="mt-4">
                            <div className="balance-header" style={{color: colorTheme}}>Up Untill {moment.isMoment(date) ? date?.format(DATE_FORMAT)  : moment.isMoment(date?.value)? date?.value?.format(DATE_FORMAT):  getPeriodEndLabel(date, contract)}</div>
                            <StatusCard  className='balance-box' status={statusCard.status} oneCardLabel={statusCard?.oneCardLabel} valueLabel={statusCard?.valueLabel}/>
                        </div>
                    }
                    <div className="container">
                        <Typography variant="h6" gutterBottom className="text-center p-2 m-0" style={{color: colorTheme, backgroundColor: '#f0f0f0'}}>
                            Time Off Requests Table
                        </Typography>
                        {/* First Resizable Table */}
                        <ResizableBox
                            width="100%"
                            height={table1Height}
                            axis="y"
                            minConstraints={[100, 100]}
                            maxConstraints={[600, 600]}
                            onResizeStop={(event, {size}) => setTable1Height(size.height)}
                            resizeHandles={["s"]}
                        >
                            <div className="row" style={{height: "100%", overflow: "hidden"}}>
                                <DataGridTable
                                    className="col-12"
                                    key={TABLES_NAMES.TIME_OFF_LIST + dataTable.length}
                                    columns={columnDefs}
                                    dataTable={dataTable}
                                    isExport={false}
                                />
                            </div>
                        </ResizableBox>
                        {/* Second Resizable Table */}
                        {balanceDataTable?.length > 0 &&
                            <>
                                <Divider
                                    className="bg-dark"
                                    style={{
                                        height: '2px',
                                        backgroundColor: '#333',
                                        margin: '10px 0',
                                        boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
                                    }}
                                />
                                <Typography variant="h6" gutterBottom className="text-center p-2 m-0" style={{color: colorTheme, backgroundColor: '#f0f0f0'}}>
                                    Balance Deduction Table
                                </Typography>
                                <ResizableBox
                                    width="100%"
                                    height={table2Height}
                                    axis="y"
                                    minConstraints={[100, 100]}
                                    maxConstraints={[600, 600]}
                                    onResizeStop={(event, {size}) => setTable2Height(size.height)}
                                    resizeHandles={["s"]}
                                >
                                    <div className="row" style={{height: "100%", overflow: "hidden"}}>
                                        <DataGridTable
                                            className="col-12"
                                            key={TABLES_NAMES.BALANCE_DEDUCTION_LIST}
                                            columns={balanceColumnDefs}
                                            dataTable={balanceDataTable}
                                            isExport={false}
                                        />
                                    </div>
                                </ResizableBox>
                            </>
                        }
                    </div>
                </div>
            }
            <Dialog className='time-off-request-dialog' open={modals[TIME_OFF_REQUEST_MODAL]}
                    toggle={toggleTimeOffEditModal}>
                <form onSubmit={(event) => handleTimeOffRequestUpdate(event, {toDate, fromDate})}>
                    <div className="m-4">
                        <h6 style={{color: colorTheme}}>Edit Time Off Request</h6>
                        <div className="mb-4">
                            {(formatFullDate(selectedTimeOffRequest?.originalFromDate) !== selectedTimeOffRequest?.fromDate) || (formatFullDate(selectedTimeOffRequest?.originalToDate) !== selectedTimeOffRequest?.toDate)
                                ? <span style={{color: 'orange', cursor: 'pointer'}}>⚠️ This is a splitted vacation
                        </span>
                                : ''}
                        </div>
                        <div className="column">
                            <DateTimePicker
                                name="fromDateTime"
                                label="From Date"
                                className="mx-1"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                            />
                            <DateTimePicker
                                name="toDateTime"
                                label="To Date"
                                className="mx-1"
                                value={toDate}
                                minDateTime={fromDate}
                                onChange={(newValue) => setToDate(newValue)}
                            />
                        </div>
                    </div>
                    <div>
                        <DialogActions className="p-3 mt-3">
                            <Button className="px-4 me-3" variant="outlined" onClick={toggleTimeOffEditModal}>Cancel</Button>
                            <Button className="px-4" variant="contained" type="submit" disabled={isInProgress}>Save</Button>
                        </DialogActions>
                    </div>
                </form>
            </Dialog>
            <Dialog className='confirmation-dialog' open={modals[TIME_OFF_CONVERT_REQUEST_MODAL]} toggle={toggleTimeOffConvertModal}>
                <form onSubmit={handleSubmit(handleTimeOffRequestConvert)}>
                    <div className="m-4" style={{color: colorTheme}}>
                        {selectedTimeOffRequest.details} leave on {moment(selectedTimeOffRequest.fromDate).format(DAY_MONTH_FORMAT)} ({moment(selectedTimeOffRequest.fromDate).format(TIME_FORMAT)} to {moment(selectedTimeOffRequest.toDate).format(TIME_FORMAT)})
                        <br/>
                        {(formatFullDate(selectedTimeOffRequest?.originalFromDate) !== selectedTimeOffRequest?.fromDate) || (formatFullDate(selectedTimeOffRequest?.originalToDate) !== selectedTimeOffRequest?.toDate)
                            ? <span style={{color: 'orange', cursor: 'pointer'}}>⚠️ This is a splitted vacation
                        </span>
                            : ''}
                    </div>
                    <div className="m-4">
                        <Controller
                            as={
                                <Select options={timeOffOptions} />
                            }
                            id="timeOffOption"
                            name="timeOffOption"
                            control = {control}
                            defaultValue={timeOffOptions[1].value}
                        />
                    </div>
                    <div>
                        <DialogActions className="p-3 mt-3">
                            <Button className="px-4 me-3" variant="outlined" onClick={toggleTimeOffConvertModal}>Cancel</Button>
                            <Button className="px-4" variant="contained" type="submit" disabled={isInProgress}>Confirm</Button>
                        </DialogActions>
                    </div>
                </form>
            </Dialog>
            <Dialog className='confirmation-dialog' open={modals[TIME_OFF_CANCEL_REQUEST_MODAL]} toggle={toggleTimeOffCancelModal}>
                <div>
                    <div className="m-4">
                        Are you sure you want to cancel{' '}
                        <span style={{color: colorTheme}}>
                            {selectedTimeOffRequest.details} leave on {moment(selectedTimeOffRequest.fromDate).format(DAY_MONTH_FORMAT)} ({moment(selectedTimeOffRequest.fromDate).format(TIME_FORMAT)} to {moment(selectedTimeOffRequest.toDate).format(TIME_FORMAT)})
                        </span>
                        ?
                    </div>
                    <div className="m-4 fw-light">
                        <span>Note: Cancellation requests require approval from managers</span>
                        <br/>
                        {(formatFullDate(selectedTimeOffRequest?.originalFromDate) !== selectedTimeOffRequest?.fromDate) || (formatFullDate(selectedTimeOffRequest?.originalToDate) !== selectedTimeOffRequest?.toDate)
                            ? <span style={{color: 'orange', cursor: 'pointer'}}>⚠️ This is a splitted vacation
                        </span>
                            : ''}
                    </div>
                    <div>
                        <DialogActions className="p-3 mt-3">
                            <Button className="px-4 me-3" variant="outlined"
                                    onClick={toggleTimeOffCancelModal}>Cancel</Button>
                            <Button className="px-4" variant="contained" disabled={isInProgress}
                                    onClick={handleTimeOffRequestCancel}>Confirm</Button>
                        </DialogActions>
                    </div>
                </div>
            </Dialog>
            <Dialog className='confirmation-dialog' open={modals[CONFIRMATION_MODAL]} toggle={toggleConfirmationModal}>
                <div>
                    <div className="m-4" style={{color: colorTheme}}>
                        {(formatFullDate(selectedTimeOffRequest?.originalFromDate) !== selectedTimeOffRequest?.fromDate) || (formatFullDate(selectedTimeOffRequest?.originalToDate) !== selectedTimeOffRequest?.toDate)
                            ? <span style={{color: 'orange', marginLeft: '0.4rem', cursor: 'pointer'}}>
                            ⚠️ This is a splitted vacation, are you sure you want to delete this ?
                        </span>
                            : 'Are you sure you want to delete this ?'}
                    </div>
                    <div>
                        <DialogActions className="p-3 mt-3">
                            <Button className="px-4 me-3" variant="outlined"
                                    onClick={toggleConfirmationModal}>Cancel</Button>
                            <Button className="px-4 bg-danger" color="secondary" variant="outlined"
                                    onClick={handleTimeOffRequestDelete} disabled={isInProgress}>Delete</Button>
                        </DialogActions>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};
const modalsProps = {
    toggleTimeOffEditModal: TIME_OFF_REQUEST_MODAL,
    toggleTimeOffConvertModal: TIME_OFF_CONVERT_REQUEST_MODAL,
    toggleTimeOffCancelModal: TIME_OFF_CANCEL_REQUEST_MODAL,
    toggleConfirmationModal: CONFIRMATION_MODAL
};
export default WithModals(TimeOffRequests, modalsProps);
